import { addDays, addWeeks, addMonths, addYears, setDate, setHours, setMinutes, setSeconds, setMilliseconds, getDaysInMonth } from 'date-fns';

/**
 * 구독 상태 타입 정의
 */
export enum SubscriptionStatus {
    PENDING = "PENDING",    // 최초 결제 확인
    ACTIVE = "ACTIVE",      // 구독 중
    PAUSED = "PAUSED",      // 구독 정지
    CANCELED = "CANCELED",  // 구독 취소
    CANCELED_WAIT = "CANCELED_WAIT",  // 구독 취소 대기
    REFUND_WAIT = "REFUND_WAIT",  // 환불 대기  
    EXPIRED = "EXPIRED",    // 구독 만료
    EOS = "EOS",            // 서비스 만료
}

/**
 * 구독 상태 그룹 타입
 */
export enum SubscriptionStatusGroup {
    PENDING = "PENDING",           // 대기 중
    COMPLETED = "COMPLETED",       // 구독 중
    PAUSED = "PAUSED",             // 구독 정지
    CANCELED = "CANCELED",         // 구독 취소
    CANCELED_WAIT = "CANCELED_WAIT",         // 구독 취소 대기
    END = "END",                   // 구독 종료
}

/**
 * 구독 상태를 구독 상태 그룹으로 매핑
 */
export const getSubscriptionStatusGroup = (subscriptionStatus: SubscriptionStatus): SubscriptionStatusGroup => {
    switch (subscriptionStatus) {
        case SubscriptionStatus.PENDING:
            return SubscriptionStatusGroup.PENDING;

        case SubscriptionStatus.ACTIVE:
            return SubscriptionStatusGroup.COMPLETED;

        case SubscriptionStatus.PAUSED:
            return SubscriptionStatusGroup.PAUSED;

        case SubscriptionStatus.CANCELED:
            return SubscriptionStatusGroup.CANCELED;

        case SubscriptionStatus.EXPIRED:
        case SubscriptionStatus.EOS:
            return SubscriptionStatusGroup.END;

        default:
            return SubscriptionStatusGroup.PENDING;
    }
};

/**
 * 구독 상태별 대표 색상 반환
 * @param subscriptionStatus - 구독 상태
 * @returns 색상 HEX 코드
 */
export const getSubscriptionStatusColor = (subscriptionStatus: SubscriptionStatus): string => {
    const statusGroup = getSubscriptionStatusGroup(subscriptionStatus);

    switch (statusGroup) {
        case SubscriptionStatusGroup.PENDING:
            return 'var(--label-tertiary, #4f4f64)'; // (대기 중)

        case SubscriptionStatusGroup.COMPLETED:
            return 'var(--primary-filled-normal, #297aff)'; // (구독 중)

        case SubscriptionStatusGroup.PAUSED:
            return 'var(--status-negative, #f83a3a)'; // (구독 정지)

        case SubscriptionStatusGroup.CANCELED:
            return 'var(--status-negative, #f83a3a)'; // (구독 취소 완료)

        case SubscriptionStatusGroup.END:
            return 'var(--status-negative, #f83a3a)'; // (구독 종료)

        default:
            return 'var(--label-tertiary, #4f4f64)'; // 기본 회색
    }
};

/**
 * 구독 상태 그룹별 색상 반환
 * @param statusGroup - 구독 상태 그룹
 * @returns 색상 HEX 코드
 */
export const getStatusGroupColor = (statusGroup: SubscriptionStatusGroup): string => {
    switch (statusGroup) {
        case SubscriptionStatusGroup.PENDING:
            return '#FFA500'; // 주황색
        case SubscriptionStatusGroup.COMPLETED:
            return '#28A745'; // 녹색
        case SubscriptionStatusGroup.CANCELED:
            return '#DC3545'; // 빨간색
        case SubscriptionStatusGroup.CANCELED_WAIT:
            return '#DC3545'; // 빨간색
        case SubscriptionStatusGroup.PAUSED:
            return '#FFC107'; // 노란색
        case SubscriptionStatusGroup.END:
            return '#6C757D'; // 회색
        default:
            return '#6C757D'; // 기본 회색
    }
};

/**
 * 구독 상태 한글 번역
 */
export const getSubscriptionStatusLabel = (subscriptionStatus: SubscriptionStatus): string => {
    switch (subscriptionStatus) {
        case SubscriptionStatus.PENDING:
            return '구독 대기 중';
        case SubscriptionStatus.ACTIVE:
            return '구독 중';
        case SubscriptionStatus.PAUSED:
            return '구독 정지';
        case SubscriptionStatus.CANCELED:
            return '구독 취소';
        case SubscriptionStatus.CANCELED_WAIT:
            return '구독 취소 대기';
        case SubscriptionStatus.EXPIRED:
            return '구독 만료';
        case SubscriptionStatus.EOS:
            return '서비스 만료';
        default:
            return '알 수 없음';
    }
};

/**
 * 타임스탬프 기반으로 다음 결제일시를 계산합니다.
 * 
 * @param baseTimestamp 계산의 기준이 되는 시점의 Epoch Milliseconds
 * @param priceType 결제 타입 (SUBSCRIPTION, FIXED_TERM, RENTAL)
 * @param billingInterval 결제 주기 단위 ("MONTH", "WEEK", "DAY", "YEAR")
 * @param billingIntervalCount 결제 주기 간격
 * @param billingDayOfMonth 월별 고정 결제일 (렌탈 전용)
 * @returns 계산된 다음 결제 예정일시의 타임스탬프. 반복이 없으면 0 반환.
 */
export const calculateNextBillingTimestamp = (
    baseTimestamp: number,
    priceType: string,
    billingInterval: string,
    billingIntervalCount: number,
    billingDayOfMonth: number = 1
): number => {
    if (baseTimestamp <= 0) {
        return 0;
    }

    // 타임스탬프를 Date 객체로 변환
    const baseDate = new Date(baseTimestamp);

    return calculateNextBillingTimestampFromDate(
        baseDate,
        priceType,
        billingInterval,
        billingIntervalCount,
        billingDayOfMonth
    );
};

/**
 * Date 객체 기반으로 다음 결제일시를 계산합니다.
 * 
 * @param baseDate 계산의 기준이 되는 날짜
 * @param priceType 결제 타입 (SUBSCRIPTION, FIXED_TERM, RENTAL)
 * @param billingInterval 결제 주기 단위 ("MONTH", "WEEK", "DAY", "YEAR")
 * @param billingIntervalCount 결제 주기 간격
 * @param billingDayOfMonth 월별 고정 결제일 (렌탈 전용)
 * @returns 계산된 다음 결제 예정일시의 타임스탬프. 반복이 없으면 0 반환.
 */
export const calculateNextBillingTimestampFromDate = (
    baseDate: Date,
    priceType: string,
    billingInterval: string,
    billingIntervalCount: number,
    billingDayOfMonth: number = 1
): number => {
    let nextBillingDate: Date | null = null;

    // 1. 다음 결제 '날짜' 계산
    if (priceType === 'SUBSCRIPTION' || priceType === 'FIXED_TERM') {
        const interval = billingInterval.toUpperCase();

        switch (interval) {
            case 'DAY':
                nextBillingDate = addDays(baseDate, billingIntervalCount);
                break;
            case 'WEEK':
                nextBillingDate = addWeeks(baseDate, billingIntervalCount);
                break;
            case 'YEAR':
                nextBillingDate = addYears(baseDate, billingIntervalCount);
                break;
            case 'MONTH':
            default:
                nextBillingDate = addMonths(baseDate, billingIntervalCount);
                break;
        }
    } else if (priceType === 'RENTAL') {
        // 렌탈의 경우 다음 달의 특정일로 설정
        const nextMonth = addMonths(baseDate, billingIntervalCount);
        const daysInMonth = getDaysInMonth(nextMonth);
        const dayToSet = Math.min(billingDayOfMonth, daysInMonth);
        nextBillingDate = setDate(nextMonth, dayToSet);
    }

    // 2. 결제 타입에 따라 '시간' 결정
    if (!nextBillingDate) {
        return 0;
    }

    if (priceType === 'RENTAL') {
        // 렌탈은 오전 6시 고정
        nextBillingDate = setHours(nextBillingDate, 6);
        nextBillingDate = setMinutes(nextBillingDate, 0);
        nextBillingDate = setSeconds(nextBillingDate, 0);
        nextBillingDate = setMilliseconds(nextBillingDate, 0);
    }
    // 구독/기간정액은 baseDate의 시간을 그대로 유지

    // 3. 최종 타임스탬프 반환
    return nextBillingDate.getTime();
};

/**
 * 결제 주기를 한글로 변환합니다.
 * 
 * @param billingInterval 결제 주기 단위
 * @param billingIntervalCount 결제 주기 간격
 * @returns 한글 결제 주기 (예: "매월", "2주마다", "매년")
 */
export const formatBillingPeriod = (
    billingInterval: string,
    billingIntervalCount: number
): string => {
    const interval = billingInterval.toUpperCase();

    if (billingIntervalCount === 1) {
        switch (interval) {
            case 'DAY':
                return '매일';
            case 'WEEK':
                return '매주';
            case 'MONTH':
                return '매월';
            case 'YEAR':
                return '매년';
            default:
                return '매월';
        }
    } else {
        switch (interval) {
            case 'DAY':
                return `${billingIntervalCount}일마다`;
            case 'WEEK':
                return `${billingIntervalCount}주마다`;
            case 'MONTH':
                return `${billingIntervalCount}개월마다`;
            case 'YEAR':
                return `${billingIntervalCount}년마다`;
            default:
                return `${billingIntervalCount}개월마다`;
        }
    }
};